import {
  type ContextValues,
  type DeepReadonly,
  type GetAtomParameters,
  getErrorMessageFromUnknown,
  isRecord,
} from "@atoms/atom-types";
import { toGraphQLRequestError } from "../graphql/GraphQLRequestError";
import { deepFreeze } from "../use-find-atom/shared";
import type { SelectorFunction } from "./makeUseAtom";

// Stable identity selector to avoid creating new function references
// biome-ignore lint/suspicious/noExplicitAny: identity function needs to accept any type
const IDENTITY_SELECTOR = (t: any) => t;

/**
 * Internal interface for parsed hook arguments.
 * @internal
 */
export interface ParsedOptions<RType extends object, TSelected> {
  hasSelector: boolean;
  selector: SelectorFunction<RType, TSelected>;
  extraContext: ContextValues | undefined;
  shouldSkip: boolean;
  getAtomParameters: GetAtomParameters | undefined;
}

/**
 * Parses and normalizes the overloaded arguments of useAtom hooks.
 * Handles both (type, options) and (type, selector, options) signatures.
 *
 * @template RType - The raw atom data type
 * @template TSelected - The selected/transformed data type
 * @param selectorOrOptions - Either a selector function or options object
 * @param options - Options object when selector is provided
 * @returns Normalized options with identity selector if none provided
 * @internal
 */
export function parseHookArguments<RType extends object, TSelected>(
  selectorOrOptions?:
    | SelectorFunction<RType, TSelected>
    | {
        extraContext?: ContextValues;
        skip?: boolean;
        getAtomParameters?: GetAtomParameters;
      },
  options?: {
    extraContext?: ContextValues;
    skip?: boolean;
    getAtomParameters?: GetAtomParameters;
  },
): ParsedOptions<RType, TSelected> {
  const hasSelector = typeof selectorOrOptions === "function";
  const selector: SelectorFunction<RType, TSelected> = hasSelector
    ? selectorOrOptions
    : (IDENTITY_SELECTOR as SelectorFunction<RType, TSelected>);

  const finalOptions = hasSelector ? options : selectorOrOptions;
  const extraContext = finalOptions?.extraContext;
  const shouldSkip = finalOptions?.skip ?? false;
  const getAtomParameters = finalOptions?.getAtomParameters;

  return {
    hasSelector,
    selector,
    extraContext,
    shouldSkip,
    getAtomParameters,
  };
}

/**
 * Parameters for retrieving and selecting state from the atom store.
 * @internal
 */
export interface GetStoreStateParams<RType extends object, TSelected> {
  atomStore: import("../AtomStore").AtomStore | undefined | null;
  type: string;
  context: ContextValues<string>;
  getAtomParameters: GetAtomParameters | undefined;
  selector: SelectorFunction<RType, TSelected>;
  shouldSkip: boolean;
}

/**
 * Result from attempting to get state from store.
 * @internal
 */
export interface GetStoreStateResult<TSelected> {
  value: TSelected | null | undefined;
  error: Error | undefined;
}

/**
 * Converts an unknown error value to an Error instance.
 * @param err - The error value to convert
 * @returns Error instance
 * @internal
 */
export function toError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }
  if (isRecord(err)) {
    // A GraphQL error of a failed subscription that the backend classified
    // keeps class, code and reference: its message is a fixed text for logs,
    // and an error screen composes what it shows from these fields.
    if (isRecord(err.extensions) && err.extensions.errorClass) {
      return toGraphQLRequestError([err], "subscribeAtom");
    }
    // An error object that is no `Error` (a GraphQL error from a response)
    // keeps its message instead of becoming "[object Object]".
    return new Error(getErrorMessageFromUnknown(err) ?? "Unknown error");
  }
  return new Error(String(err as string | number | boolean | null | undefined));
}

/**
 * Retrieves atom data from the store and applies selector function.
 * Catches and handles errors from both store access and selector execution.
 *
 * @template RType - The raw atom data type
 * @template TSelected - The selected/transformed data type
 * @param params - Parameters including store, selector, and error handlers
 * @returns Object with value and error
 * @internal
 */
export function getSelectedStateFromStore<RType extends object, TSelected>(
  params: GetStoreStateParams<RType, TSelected>,
): GetStoreStateResult<TSelected> {
  try {
    const currentStoreValue = params.shouldSkip
      ? undefined
      : params.atomStore?.getAtom(
          params.type,
          params.context,
          params.getAtomParameters,
        );
    return {
      value:
        currentStoreValue === null
          ? null
          : currentStoreValue
            ? params.selector(currentStoreValue)
            : undefined,
      error: undefined,
    };
  } catch (err) {
    return {
      value: undefined,
      error: toError(err),
    };
  }
}

/**
 * Updates error state only if the error message has changed.
 * Prevents unnecessary re-renders when the same error occurs multiple times.
 *
 * @param error - The new error to set
 * @param lastErrorMessage - Ref to track the last error message
 * @param setError - State setter for error
 * @returns true if error was updated, false otherwise
 * @internal
 */
export function updateErrorIfChanged(
  error: Error,
  lastErrorMessage: React.RefObject<string | undefined>,
  setError: (error: Error | undefined) => void,
): boolean {
  const errorMsg = error.message;
  if (errorMsg !== lastErrorMessage.current) {
    lastErrorMessage.current = errorMsg;
    setError(error);
    return true;
  }
  return false;
}

/**
 * React refs used by the update callback to maintain state across renders.
 * @internal
 */
export interface UpdateCallbackRefs<TSelected, RType extends object> {
  latestSelectedState: React.RefObject<
    DeepReadonly<TSelected> | null | undefined
  >;
  latestSelector: React.RefObject<((arg0: RType) => TSelected) | undefined>;
  latestSubscriptionCallbackError: React.RefObject<Error | undefined>;
}

/**
 * Actions available to the update callback for state management.
 * @internal
 */
export interface UpdateCallbackActions {
  equalityFn: (a: unknown, b: unknown) => boolean;
  forceRender: () => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Creates a callback function for handling atom subscription updates.
 * This callback is invoked whenever the atom value changes in the store.
 *
 * The callback:
 * - Handles null values (atom not found)
 * - Applies selector function to transform data
 * - Uses equality check to prevent unnecessary re-renders
 * - Catches and reports any errors
 *
 * @template RType - The raw atom data type
 * @template TSelected - The selected/transformed data type
 * @param refs - React refs for maintaining state across renders
 * @param actions - Functions for updating component state
 * @returns Callback function for atom subscription updates
 * @internal
 */
export function createCheckForUpdatesCallback<RType extends object, TSelected>(
  refs: UpdateCallbackRefs<TSelected, RType>,
  actions: UpdateCallbackActions,
): (currentValue: RType | null | undefined) => void {
  // `undefined`: nothing received yet. It is selected as `undefined`.
  return (currentValue: RType | null | undefined): void => {
    try {
      if (actions.onSuccess) {
        actions.onSuccess();
      }

      if (currentValue === null) {
        // Avoid render loops: if we're already in the "not found" state, don't
        // force another render for the same null value.
        if (refs.latestSelectedState.current === null) {
          return;
        }
        refs.latestSelectedState.current = null;
        actions.forceRender();
        return;
      }

      const newSelectedState =
        currentValue && refs.latestSelector.current?.(currentValue);

      if (
        actions.equalityFn(newSelectedState, refs.latestSelectedState.current)
      ) {
        return;
      }
      refs.latestSelectedState.current = deepFreeze(newSelectedState);
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
    } catch (err: any) {
      refs.latestSubscriptionCallbackError.current = err;
      if (actions.onError && err instanceof Error) {
        actions.onError(err);
      }
      actions.forceRender();
      return;
    }

    actions.forceRender();
  };
}
